<template>
    <div>
        <app-bar :title="article.name"/>

        <v-layout>
            <v-progress-linear
                v-if="overlay"
                absolute
                indeterminate
            ></v-progress-linear>

            <div class="pt-8 pb-12 px-8">
                <div class="pb-10">
                    <v-btn
                        @click="edit"
                        class="sm mr-5"
                        color="primary"
                    >{{ t('general_action_edit') }}
                    </v-btn>
                    <v-btn
                        class="sm mr-5"
                        color="primary"
                        outlined
                        @click.prevent="suspend(article)"
                    >
                        {{ article.published ? t('general_action_suspend') : t('general_action_unsuspend') }}
                    </v-btn>
                    <confirm-modal
                        :title="t('general_title_confirm_action')"
                        @agree="del(article)"
                    >
                        <template #default="{ on, attrs }">
                            <v-btn
                                class="sm"
                                color="orange"
                                outlined
                                v-on="on"
                                v-bind="attrs"
                            >{{ t('general_action_delete') }}</v-btn>
                        </template>
                    </confirm-modal>
                </div>
                <div class="float-left mr-10" style="width: 394px; max-width: 394px">
                    <img
                        v-if="article.photoUrl"
                        :src="article.photoUrl"
                        width="394"
                    />
                    <div class="form-subtitle">{{ t('health_article_label_lead_paragraph') }}:</div>
                    <div class="form-subtitle-description" v-html="article.leadParagraph"></div>
                    <div class="form-subtitle">{{ t('health_article_label_content') }}:</div>
                    <div class="form-subtitle-description" v-html="article.content"></div>
                </div>
                <div class="float-left" style="max-width: calc(100% - 434px)">
                    <card-unit :items="[
                        { text: t('health_article_label_section'), value: article.section.name },
                        { text: t('general_label_creation_date'), value: article.createdAt.format('DD/MM/YYYY') },
                        { text: t('health_article_label_published'), value: article.published ? t('general_text_yes') : t('general_text_no') },
                        { text: t('general_label_publishing_date'), value: article.publishedAt.format('DD/MM/YYYY HH:mm') },
                    ]">
                        <template #title>{{ t('health_article_label_id') }}: {{ article.id }}</template>
                    </card-unit>
                </div>
            </div>
        </v-layout>
    </div>
</template>

<script lang="ts">
    import { computed, defineComponent, onMounted, reactive, toRefs } from '@vue/composition-api'
    import { useI18n } from 'vue-i18n-composable'
    import { useStore } from '@/store'
    import { useRouter } from '@/composition-api'
    import { useEnums } from '@/core/defaults'
    import { HealthArticle } from '@/core/models/health-article'
    import moment from 'moment/moment'
    import ConfirmModal from '@/components/ConfirmModal.vue'
    import CardUnit from '@/components/CardUnit.vue'

    export default defineComponent({
        components: {
            ConfirmModal,
            CardUnit
        },

        /**
         * Component constructor.
         *
         * @param props
         */
        setup(props) {
            const store = useStore()
            const router = useRouter()
            const i18n = useI18n()
            const id = router.currentRoute.params['id']

            const state = reactive({
                overlay: true,
                currentTab: <number | null>null,
                photo: <File | undefined>undefined,
            })

            const capture = () => {
                store.dispatch('healthArticle/get', { id: id, payload: { published: '', relations: 'section' } }).then(() => {
                    state.overlay = false
                })
            }

            const articleModel = computed(() => <HealthArticle>store.getters['healthArticle/get'])

            const suspend = (articleModel: HealthArticle) => {
                state.overlay = true
                store.dispatch('healthArticle/update', {
                    id: articleModel.id,
                    payload: { published: ! articleModel.published }
                }).then(() => {
                    store.dispatch('notify', { color: 'success', message: i18n.t('general_notice_data_updated') })
                    capture()
                })
            }

            const edit = () => router.push({ name: 'articles.show', params: { id: <any>articleModel.value.id } })

            const del = (articleModel: HealthArticle) => {
                state.overlay = true
                store.dispatch('healthArticle/delete', { id: articleModel.id }).then(() => {
                    router.push({ name: 'articles' })
                })
            }

            onMounted(() => {
                capture()
            })

            return {
                capture,
                article: articleModel.value,
                moment,
                edit,
                del,
                suspend,
                ...useI18n(),
                ...useEnums(),
                ...toRefs(state)
            }
        }
    })
</script>
